/**
 * İSİM ÇAKIŞMASI ÖLÇÜTÜ — kurum kimliği taklit edilemez (madde 24 · 4. kalem).
 *
 * Kullanıcının kararı ölçütün kaynağıdır: *"Sabancı Holding adına herhangi biri
 * etkinlik açamamalı."* Doğrulama bir rozet katmanıdır; kapı YALNIZ isim
 * çakışmasında iner (`docs/madde-24-kimlik-modeli-karari.md`).
 *
 * Ölçüt üç kademelidir ve kademe sırası bilinçlidir:
 *
 *  1. `identical` — İSKELET AYNI. İki ad, görünür farkları soyulduğunda aynı
 *     dizgeye iner. Homoglif saldırısı burada yakalanır: `Sabancı`, `Sabanci`,
 *     `SABANCI`, `Sаbancı` (Kiril `а` = U+0430), araya U+200B konmuş yazım ve
 *     `5abanc1` hepsi `sabanci` iskeletine iner.
 *  2. `near` — DÜZENLEME MESAFESİ eşiğin altında. Tek harf ekleme/silme/değişme
 *     ya da komşu harf takası (`Sabnacı`) yakalanır. Eşik uzunlukla büyür,
 *     çünkü kısa adlarda bir harf gerçek bir anlam farkıdır (`Ada` ≠ `Ata`).
 *  3. `contains` — KORUNAN ADIN AYIRT EDİCİ SÖZCÜKLERİNİN HEPSİ adayda geçiyor.
 *     `Sabancı Tekstil` mesafeye takılmaz ama `sabanci` sözcüğünü taşır.
 *     Bu kademe "meşru olabilir"in ta kendisidir; bu yüzden sonucu red değil,
 *     doğrulama başvurusudur (karar noktası servistedir).
 *
 * ÖLÇÜT NEDEN BÖYLE: mesafe tek başına yetmez (uzun adlarda taklit ucuzdur),
 * içerme tek başına yetmez (yazım hatası taklidi kaçar), iskelet tek başına
 * yetmez (tek harf eklemek yeter). Üçü birlikte, "gözle bakan biri karıştırır
 * mı" sorusunun makinede karşılığıdır.
 *
 * YANLIŞ POZİTİF UCUZ DEĞİLDİR (düzeltme turu): eşleşen ad yasaklanmaz,
 * doğrulama yoluna çıkar — ama doğrulama İNSAN ONAYIDIR ve arada kurum yayın
 * açamaz. Yani "kapı sıkı olsun, nasılsa çıkışı var" savunulamaz; kategori
 * çapında kilit gerçek bir duvardır. Kademelerin kapsamı buna göre daraltıldı:
 * `contains` yalnız GERÇEK MARKA sözcüğü üzerinden çalışır, `identical` de
 * hukuki ek soyarken markayı taban altına düşürmez.
 *
 * SÖZLÜK AYRI DOSYADA: hangi sözcüğün ayırt edici olmadığı bir VERİ kararıdır
 * ve ölçütten hızlı büyür — `organization-name-vocabulary.ts`.
 */
import { ORGANIZATION_NAME_CONFUSABLE_CLASSES, ORGANIZATION_NAME_GENERIC_TOKENS, ORGANIZATION_NAME_INVISIBLE_CODEPOINTS, ORGANIZATION_NAME_LEGAL_SUFFIXES, } from './organization-name-vocabulary';
const INVISIBLE = new Set(ORGANIZATION_NAME_INVISIBLE_CODEPOINTS);
/** Karakter → sınıf temsilcisi (tablodan TEK seferde türetilir). */
const CONFUSABLE_MAP = new Map(Object.entries(ORGANIZATION_NAME_CONFUSABLE_CLASSES).flatMap(([canonical, members]) => [...members].map((character) => [character, canonical])));
/**
 * İKİ TABLO DA KATLANARAK KURULUR — insan okunur yazılır, makine katlanmış
 * karşılaştırır. Elle katlanmış yazılsaydı tablo `fold()` ile sessizce
 * ayrışırdı: `Holding` iskelette `hoiding`tir (`l` sınıfı `i`ye iner) ve elle
 * yazılan `holding` HİÇBİR ZAMAN eşleşmezdi. Hukuki ek soyma da tür sözcüğü
 * ayıklaması da bu yüzden aynı fonksiyondan geçer.
 */
const foldToken = (value) => fold(value)
    .replace(/[.'’`´]/g, '')
    .replace(/[^a-z0-9]/g, '');
const LEGAL_SET = new Set(ORGANIZATION_NAME_LEGAL_SUFFIXES.map(foldToken));
const GENERIC_SET = new Set(ORGANIZATION_NAME_GENERIC_TOKENS.map(foldToken));
/** Ayırt edici sayılmak için en az bu kadar harf gerekir (`ege`, `abc` sayılmaz). */
const DISTINCTIVE_MIN_LENGTH = 4;
/** Karakter katlaması: görünmezleri at, sınıfa indir, kalan aksanı düşür. */
function fold(value) {
    const mapped = [...value.normalize('NFKC')]
        .filter((character) => !INVISIBLE.has(character.codePointAt(0) ?? 0))
        .map((character) => CONFUSABLE_MAP.get(character) ?? character)
        .join('');
    return mapped.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}
/**
 * ADIN SÖZCÜKLERİ — katlanmış, noktalama soyulmuş, hukuki ekler atılmış.
 *
 * Nokta AYIRICI DEĞİL, silinir: `A.Ş.` tek sözcük (`as`) olmalıdır, iki harf
 * değil. Kalan her noktalama ve boşluk ayırıcıdır.
 *
 * AYRI SÖZCÜK SOYMASINDA DA TABAN VAR (düzeltme turu, bulgu 3). Yapışık ekte
 * `GLUED_MIN_REMAINDER` vardı, ayrı sözcükte YOKTU ve asimetri kasıtlı değildi:
 * `Ata Vakfı`, `Ata Derneği`, `Ata Holding` üçü de `ata` iskeletine iniyor ve
 * BİREBİR çakışıyordu — üç ayrı kurum, tek iskelet. Aynı şekilde `Ada A.Ş.` ile
 * `Ada Ltd.` çakışıyordu.
 *
 * KURAL: soyma iskeleti `DISTINCTIVE_MIN_LENGTH`in altına düşürüyorsa, soyulan
 * ekler SIRAYLA geri alınır ve taban dolar dolmaz durulur. Böylece
 * `Ata Holding` → `atahoiding` olurken `Ata Holding A.Ş.` de `atahoiding`
 * kalır: hukuki ek yığmak hâlâ kaçış yolu değildir. Tamamı hukuki ekten ibaret
 * ad (`Holding`, `A.Ş.`) da aynı kuralla boş listeye inmez.
 */
export function organizationNameTokens(name) {
    const folded = fold(name).replace(/[.'’`´]/g, '');
    const raw = folded
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length > 0)
        .map(stripGluedLegalSuffix);
    const stripped = raw.filter((token) => !LEGAL_SET.has(token));
    if (skeletonLength(stripped) >= DISTINCTIVE_MIN_LENGTH)
        return stripped;
    const kept = [];
    let length = 0;
    for (const token of raw) {
        if (LEGAL_SET.has(token) && length >= DISTINCTIVE_MIN_LENGTH)
            continue;
        kept.push(token);
        length += token.length;
    }
    return kept;
}
const skeletonLength = (tokens) => tokens.reduce((total, token) => total + token.length, 0);
/** Yapışık hukuki ekten sonra geriye kalması gereken en az harf sayısı. */
const GLUED_MIN_REMAINDER = 4;
/**
 * Yapışık soymaya YALNIZ üç harf ve üzeri ekler girer. İki harfliler (`as`,
 * `co`, `ag`, `oy`) gerçek sözcüklerin içinde çok sık geçiyor: `Sabancö`
 * iskeleti `sabanco`dur ve `co` soyulsaydı `saban`a inip korunan addan
 * UZAKLAŞIRDI. İki harfliler ayrı sözcük olarak (`A.Ş.`) yine soyulur.
 */
const GLUED_MIN_SUFFIX = 3;
/**
 * YAPIŞIK HUKUKİ EK — boşluksuz yazım kaçışını kapatır.
 *
 * `Sabancı Holding` ile `SabancıHolding` ekranda aynı kurumdur ama sözcüğe
 * bölünmediği için ek soyma çalışmazdı ve `sabancihoiding` iskeleti korunan
 * `sabanci` ile çakışmıyordu. Ek, sözcüğün SONUNDAN tekrar tekrar soyulur.
 *
 * GERİYE EN AZ DÖRT HARF KALMALIDIR; yoksa soyma anlamı yer: `Havas` → `hav`,
 * `disco` → `dis` olurdu. Bu sınır, gerçek markaların hukuki ek sanılmasını
 * engeller.
 */
function stripGluedLegalSuffix(token) {
    let current = token;
    let changed = true;
    while (changed) {
        changed = false;
        for (const suffix of LEGAL_SET) {
            if (suffix.length >= GLUED_MIN_SUFFIX &&
                current.length - suffix.length >= GLUED_MIN_REMAINDER &&
                current.endsWith(suffix)) {
                current = current.slice(0, current.length - suffix.length);
                changed = true;
                break;
            }
        }
    }
    return current;
}
/**
 * KARŞILAŞTIRMA İSKELETİ — veritabanında saklanan ve indekslenen değer.
 *
 * `organizations.name_normalized` bu fonksiyonun çıktısıdır ve TÜREVİ
 * repository katmanında yazılır (servis yazamaz), böylece ad ile iskelet
 * ayrışamaz. Boşluk taşımaz: `SabancıHolding` ile `Sabancı Holding` aynı
 * iskelete iner.
 */
export function normalizeOrganizationName(name) {
    return organizationNameTokens(name).join('');
}
/**
 * MESAFE EŞİĞİ — iskelet uzunluğuyla büyür.
 *
 * 5 harften kısa adda eşik 0'dır: `Ada` ile `Ata` arasında bir harf var ve
 * ikisi de meşru addır; orada yalnız BİREBİR iskelet çakışması konuşur.
 * 5-9 harfte 1, 10 ve üstünde 2. Üst sınır 2'de tutulur: daha yükseği uzun
 * adlarda alakasız kurumları birbirine bağlamaya başlar.
 */
export function organizationNameDistanceThreshold(length) {
    if (length < 5)
        return 0;
    if (length < 10)
        return 1;
    return 2;
}
/**
 * Damerau-Levenshtein (bitişik takas dahil), eşik aşılınca ERKEN çıkar.
 *
 * Takas neden dahil: taklidin en ucuz biçimlerinden biri iki harfi yer
 * değiştirmektir (`Sabnacı`); düz Levenshtein bunu 2 sayar ve kısa adlarda
 * kaçırır.
 */
export function boundedNameDistance(a, b, limit) {
    if (a === b)
        return 0;
    if (Math.abs(a.length - b.length) > limit)
        return limit + 1;
    /* İndeksler daima menzil içinde; `?? 0` yalnız noUncheckedIndexedAccess içindir. */
    const cell = (row, index) => row[index] ?? 0;
    let previous2 = new Array(b.length + 1).fill(0);
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
        const current = new Array(b.length + 1).fill(0);
        current[0] = i;
        let rowBest = i;
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let value = Math.min(cell(current, j - 1) + 1, cell(previous, j) + 1, cell(previous, j - 1) + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                value = Math.min(value, cell(previous2, j - 2) + 1);
            }
            current[j] = value;
            rowBest = Math.min(rowBest, value);
        }
        if (rowBest > limit)
            return limit + 1;
        previous2 = previous;
        previous = current;
    }
    return cell(previous, b.length);
}
/**
 * Korunan adın MARKA GÜCÜ olan sözcükleri. Üç eleme birden uygulanır:
 * kısa sözcük · hukuki ek · jenerik sözcük (sektör/kurum türü/coğrafya).
 *
 * HUKUKİ EK ELEMESİ ZORUNLU (düzeltme turu, bulgu 3'ün yan etkisi): taban
 * kuralı hukuki eki iskelette bırakabiliyor (`Ata Holding` → `ata`+`hoiding`).
 * Elemesek `hoiding` ayırt edici sayılır ve `Ata Holding` doğrulanınca
 * "Holding" taşıyan HER kurum bloke olurdu.
 */
export function distinctiveOrganizationNameTokens(name) {
    return organizationNameTokens(name).filter((token) => token.length >= DISTINCTIVE_MIN_LENGTH && !GENERIC_SET.has(token) && !LEGAL_SET.has(token));
}
/**
 * ADAY ile KORUNAN AD karşılaştırması. Eşleşme yoksa `null`.
 *
 * `protectedName` DOĞRULANMIŞ kurumun adıdır; yön simetrik değildir, çünkü
 * `contains` kademesi korunan adın sözcüklerini adayda arar (tersi değil):
 * `Sabancı Tekstil` yeni kurulurken `Sabancı Holding` korunur, tersi olmaz.
 */
export function matchOrganizationName(candidateName, protectedName) {
    const candidate = normalizeOrganizationName(candidateName);
    const target = normalizeOrganizationName(protectedName);
    if (candidate.length === 0 || target.length === 0)
        return null;
    if (candidate === target)
        return { kind: 'identical', distance: 0, sharedTokens: [] };
    const threshold = organizationNameDistanceThreshold(Math.min(candidate.length, target.length));
    if (threshold > 0) {
        const distance = boundedNameDistance(candidate, target, threshold);
        if (distance <= threshold)
            return { kind: 'near', distance, sharedTokens: [] };
    }
    /*
     * `contains` TEK BAŞINA BİR JENERİK SÖZCÜĞE DAYANAMAZ (ürün kararı, düzeltme
     * turu). Ayırt edici küme jenerik/hukuki/kısa sözcükler ayıklandıktan sonra
     * BOŞ kalıyorsa kademe HİÇ tetiklenmez — çünkü `every` boş kümede daima
     * doğrudur ve kural "her ada uyar" hâline gelirdi. Küme tek elemanlı ama
     * eleman gerçek markaysa (`Sabancı Holding` → `sabanci`) kademe çalışır.
     */
    const distinctive = distinctiveOrganizationNameTokens(protectedName);
    if (distinctive.length > 0) {
        const candidateTokens = new Set(organizationNameTokens(candidateName));
        if (distinctive.every((token) => candidateTokens.has(token))) {
            return { kind: 'contains', distance: null, sharedTokens: distinctive };
        }
    }
    return null;
}
//# sourceMappingURL=organization-name-similarity.js.map